import { useCallback, useEffect, useRef, useState } from "react"

import {
  trackAppletCreationCompleted,
  trackBuildAutomationPrefillAbandoned,
  trackBuildAutomationPrefillSent,
  trackBuildAutomationPrefillShown,
  trackConversationalUIAppletCreated,
  trackConversationalUISessionEnded,
  trackConversationalUISessionStarted,
  trackConversationalUITurnCompleted,
  trackTryItPrefillAbandoned,
  trackTryItPrefillSent,
  trackTryItPrefillShown,
  type AppletCreationMode,
  type BuildAutomationPrefillPayload,
  type ConversationEntryPoint,
  type ConversationGoalType,
  type ConversationOutcome,
  type TryItPrefillPayload,
} from "app/utils/mixpanel_tracking"

import type { User } from "../utils/interfaces"
import type { BuildAutomationPrefill, ChatPrefill, TqaPrefill } from "../utils/tqa_prefill"
import Conversation from "./conversation"
import { recordCreatedApplet } from "./created_applet_storage"
import Intro, { type IntroCopy } from "./intro"
import { isCreateAppletSuccessResult } from "./sse_events"
import type {
  AppletCreatedMetadata,
  SessionEndedMetadata,
  SessionStartedMetadata,
  TurnCompletedMetadata,
  UpgradeRequest,
} from "./types"

interface Props {
  active: boolean
  showUpgradeModal: (request?: UpgradeRequest) => void
  trackCreationStarted: (creationMode: AppletCreationMode) => void
  creationAttemptId: () => string
  creationAttemptMode: () => AppletCreationMode
  user: User
  introCopy: IntroCopy
  // A prompt handed off from another surface (e.g. the /explore hero). With
  // autoStart it skips the intro and sends immediately; without it, it only
  // pre-fills the intro so the user still confirms the send.
  initialPrompt?: string
  autoStart?: boolean
  entryPoint?: ConversationEntryPoint
  // A Try it / Build an automation link's chat_prefill_* params. When present it
  // wins over the hero hand-off above: the URL states this navigation's intent.
  chatPrefill?: ChatPrefill | null
}

const GOAL_TYPE: ConversationGoalType = "create_applet"

const toTryItPayload = (prefill: TqaPrefill): TryItPrefillPayload => ({
  sourceService: prefill.serviceName,
  sourceServiceModule: prefill.serviceModuleName,
  sourceTqa: prefill.tqaName,
  tqaType: prefill.type,
  activeTab: prefill.activeTab,
})

const toBuildAutomationPayload = (prefill: BuildAutomationPrefill): BuildAutomationPrefillPayload => ({
  sourceTrigger: prefill.triggerName,
  sourceTriggerService: prefill.triggerServiceName,
  sourceAction: prefill.actionName,
  sourceActionService: prefill.actionServiceName,
  triggerServiceModule: prefill.triggerServiceModuleName,
  actionServiceModule: prefill.actionServiceModuleName,
  selectionChanged: prefill.selectionChanged,
})

const trackPrefillShown = (prefill: ChatPrefill) =>
  prefill.kind === "build_automation"
    ? trackBuildAutomationPrefillShown(toBuildAutomationPayload(prefill))
    : trackTryItPrefillShown(toTryItPayload(prefill))

const trackPrefillSent = (prefill: ChatPrefill, promptEdited: boolean) =>
  prefill.kind === "build_automation"
    ? trackBuildAutomationPrefillSent({
        ...toBuildAutomationPayload(prefill),
        promptEdited,
      })
    : trackTryItPrefillSent({ ...toTryItPayload(prefill), promptEdited })

const trackPrefillAbandoned = (prefill: ChatPrefill) =>
  prefill.kind === "build_automation"
    ? trackBuildAutomationPrefillAbandoned(toBuildAutomationPayload(prefill))
    : trackTryItPrefillAbandoned(toTryItPayload(prefill))

// Narrower than AppletCreationMode so the value also serves as the ConversationEntryPoint.
const prefillCreationMode = (prefill: ChatPrefill): Extract<AppletCreationMode, ConversationEntryPoint> =>
  prefill.kind === "build_automation" ? "build_automation" : "try_it"

function deriveOutcome(metadata: SessionEndedMetadata, active: boolean): ConversationOutcome {
  if (metadata.goalAchieved) return "success_goal_achieved"
  if (metadata.errorBlocking) return "error_blocking"
  if (!active) return "redirected_to_classic"
  return "abandoned_mid_flow"
}

const ChatTab = (props: Props) => {
  const { trackCreationStarted, creationAttemptId, creationAttemptMode, chatPrefill } = props

  // A chat prefill from the URL describes this navigation; a hero hand-off found
  // in storage may be left over from an earlier one, so the prefill wins outright.
  const heroPrompt = chatPrefill ? undefined : props.initialPrompt
  const autoStartPrompt = props.autoStart && !chatPrefill ? (props.initialPrompt ?? "") : ""
  const [stage, setStage] = useState<"intro" | "conversation">(autoStartPrompt ? "conversation" : "intro")
  const [seedMessage, setSeedMessage] = useState(autoStartPrompt)

  const entryPoint: ConversationEntryPoint = chatPrefill
    ? prefillCreationMode(chatPrefill)
    : (props.entryPoint ?? "create_applet")

  // Auto-started conversations bypass Intro's submit handler, so mirror its
  // tracking side effects here (once — StrictMode re-runs mount effects).
  const autoStartTrackedRef = useRef(false)
  useEffect(() => {
    if (!autoStartPrompt || autoStartTrackedRef.current) return
    autoStartTrackedRef.current = true
    trackCreationStarted("ai")
    if (props.user.tier === "free") {
      window.App.Utils?.logCustomDatadogAction?.("free_user_ai_prompt", {
        message: autoStartPrompt,
      })
    }
  }, [])

  const prefillShownRef = useRef(false)
  const prefillSentRef = useRef(false)
  const prefillAbandonedRef = useRef(false)

  useEffect(() => {
    if (!chatPrefill || prefillShownRef.current) return
    prefillShownRef.current = true
    trackPrefillShown(chatPrefill)
  }, [chatPrefill])

  // Abandoned = left the page with the prefill shown and never sent it. The
  // once-ref keeps a bfcache restore (pagehide fires again after Back) from
  // counting one impression as two abandons.
  useEffect(() => {
    if (!chatPrefill || stage !== "intro") return
    const handlePagehide = () => {
      if (prefillSentRef.current || prefillAbandonedRef.current) return
      prefillAbandonedRef.current = true
      trackPrefillAbandoned(chatPrefill)
    }
    window.addEventListener("pagehide", handlePagehide)
    return () => window.removeEventListener("pagehide", handlePagehide)
  }, [chatPrefill, stage])

  const handleIntroSubmit = useCallback(
    (prompt: string) => {
      if (chatPrefill) {
        prefillSentRef.current = true
        trackPrefillSent(chatPrefill, prompt !== chatPrefill.prompt)
        trackCreationStarted(prefillCreationMode(chatPrefill))
      } else {
        trackCreationStarted("ai")
      }
      setSeedMessage(prompt)
      setStage("conversation")
    },
    [trackCreationStarted, chatPrefill],
  )

  const handleAppletCreated = useCallback(
    (applet: unknown, metadata: AppletCreatedMetadata) => {
      if (isCreateAppletSuccessResult(applet)) {
        trackConversationalUIAppletCreated({
          conversationId: metadata.conversationId,
          appletId: applet.applet_slug,
          promptsToCreation: metadata.promptsToCreation,
          conversationDurationSeconds: metadata.conversationDurationSeconds,
        })
        trackAppletCreationCompleted({
          creationMode: creationAttemptMode(),
          creationAttemptId: creationAttemptId(),
          entrypoint: "diy_composer",
          appletId: applet.applet_slug,
        })
        recordCreatedApplet({
          conversationId: metadata.conversationId,
          appletId: applet.applet_slug,
        })
      }
    },
    [creationAttemptId, creationAttemptMode],
  )

  const handleSessionStarted = useCallback(
    (metadata: SessionStartedMetadata) => {
      trackConversationalUISessionStarted({
        conversationId: metadata.conversationId,
        goalType: GOAL_TYPE,
        entryPoint,
        sessionStartTimestamp: metadata.startedAtIso,
      })
    },
    [entryPoint],
  )

  const handleSessionEnded = useCallback(
    (metadata: SessionEndedMetadata) => {
      trackConversationalUISessionEnded({
        conversationId: metadata.conversationId,
        totalUserPrompts: metadata.totalUserPrompts,
        totalAiResponses: metadata.totalAiResponses,
        conversationOutcome: deriveOutcome(metadata, props.active),
        goalAchieved: metadata.goalAchieved,
        exitMethod: metadata.exitMethod,
        sessionEndTimestamp: metadata.endedAtIso,
        sessionDurationSeconds: metadata.durationSeconds,
        viaBeacon: metadata.viaBeacon,
      })
    },
    [props.active],
  )

  const handleTurnCompleted = useCallback((metadata: TurnCompletedMetadata) => {
    trackConversationalUITurnCompleted({
      conversationId: metadata.conversationId,
      turnNumber: metadata.turnNumber,
      responseTime: metadata.responseTimeMs,
      userActionAfterResponse: metadata.userAction,
      errorOccurred: metadata.errorOccurred,
      mcpToolCalled: metadata.mcpToolCalled,
      mcpToolSuccess: metadata.mcpToolSuccess,
    })
  }, [])

  if (stage === "conversation") {
    return (
      <Conversation
        initialMessage={seedMessage}
        intention="ifttt_assistant"
        onAppletCreated={handleAppletCreated}
        onSessionStarted={handleSessionStarted}
        onSessionEnded={handleSessionEnded}
        onTurnCompleted={handleTurnCompleted}
        username={props.user.login}
        onUpgradeRequest={props.showUpgradeModal}
      />
    )
  }

  return (
    <Intro
      active={props.active}
      user={props.user}
      onSubmit={handleIntroSubmit}
      copy={props.introCopy}
      initialPrompt={chatPrefill?.prompt ?? heroPrompt}
    />
  )
}

export default ChatTab
